import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Tab,
  Tabs,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  Tooltip, // Import Tooltip
  Fab, // Import Floating Action Button
} from "@mui/material";
import {
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Cancel,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { SEO } from "../../components/common/SEO";
import {
  deleteLoginCookieAndSession,
  getLoginCookieAndSession,
  getSellerName,
  sendConfirmationEmail,
  setSession,
  getSession,
  deleteSession,
} from "../../utils";
import { USER_JUST_LOGGED_IN, USER_JUST_LOGGED_OUT } from "../../constants";
import SnackbarAlert, {
  SnackbarRef,
} from "../../components/common/SnackbarAlert";
import {
  fetchNotificationsByUser,
  updateNotificationsById,
} from "../../redux/actions/notificationActions";
import { Notification } from "../../redux/types/notificationTypes";
import {
  SORT_BY,
} from "../../redux/actions/productActions";
import { fetchCartReset } from "../../redux/actions/cartAction";
import { Dashboard } from "../../components/account/Dashboard";
import { MyListing } from "../../components/account/MyListing";
import { Purchase } from "../../components/account/Purchase";
import { Sold } from "../../components/account/Sold";
import { Wishlist } from "../../components/account/Wishlist";
import { Settings } from "../../components/account/Settings";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";
import { createStripeAccountLink } from "../Seller/seller-setup-api";

const Account: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sellRoute, isApproved } = useStripeAccountStatus();

  const [selectedTab, setSelectedTab] = useState(() => {
    const state = location.state as { tab?: number } | null;
    return typeof state?.tab === "number" ? state.tab : 0;
  });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [defaultFilterInMyListing, setDefaultFilterInMyListing] =
    useState<SORT_BY>(SORT_BY.LATEST);
  const [verificationLinkLoading, setVerificationLinkLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const profileId = getLoginCookieAndSession();

  const dispatch = useDispatch();
  const { loading, users } = useSelector(
    (state: RootState) => state.users
  );

  const { notificationsData } = useSelector(
    (state: RootState) => state.notifications
  );

  useEffect(() => {
    if (profileId) {
      dispatch(fetchNotificationsByUser(Number(profileId)));
      dispatch(fetchUserByUniqueId(Number(profileId)));
    } else {
      deleteLoginCookieAndSession();
      navigate("/");
    }
  }, [dispatch]);

  useEffect(() => {
    setNotifications(notificationsData.notifications);
  }, [notificationsData.notifications]);

  // useStripeAccountStatus polls in the background until the seller account is
  // approved. Once it flips, re-fetch the user doc so the stale `isVerified` in
  // redux catches up (the webhook sets it server-side while this page may already
  // be open) — that refresh is what lets the effect below auto-clear the
  // "Confirm Your Account" notification without the user having to reload the page.
  useEffect(() => {
    if (isApproved && profileId) {
      dispatch(fetchUserByUniqueId(Number(profileId)));
    }
  }, [isApproved, profileId, dispatch]);

  // Auto-clear "Confirm Your Account" notification when user is verified
  useEffect(() => {
    if (
      users &&
      users.length > 0 &&
      users[0].isVerified &&
      notificationsData.notifications.length > 0
    ) {
      const confirmNotifications = notificationsData.notifications.filter(
        (n) => n.title?.includes("Confirm Your Account")
      );
      confirmNotifications.forEach((notification) => {
        dispatch(updateNotificationsById(notification.documentId));
      });
      if (confirmNotifications.length > 0) {
        setNotifications((prev) =>
          prev.filter(
            (n) => !n.title?.includes("Confirm Your Account")
          )
        );
      }
    }
  }, [users, notificationsData.notifications, dispatch]);

  useEffect(() => {
    if (getSession(USER_JUST_LOGGED_IN)) {
      snackbarRef.current?.showSnackbar("Signed in successfully", "success");
      const timer = setTimeout(() => {
        deleteSession(USER_JUST_LOGGED_IN);
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setDefaultFilterInMyListing(SORT_BY.LATEST);
  };

  const handleLogout = () => {
    deleteLoginCookieAndSession();
    setSession(USER_JUST_LOGGED_OUT, USER_JUST_LOGGED_OUT);
    dispatch(fetchCartReset());
    navigate("/");
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismissNotification = (id: string) => {
    dispatch(updateNotificationsById(id));
    setNotifications((prev) =>
      prev.filter((notification) => notification.documentId !== id)
    );
  };

  // Same fresh-account-link flow the action-required email's button uses, rather than
  // sending the user to our internal seller-setup page. Account Links are single-use
  // and expire quickly, so we can't reuse the URL from the email — we generate a new
  // one on click, right before it's needed.
  const handleCompleteVerificationOnStripe = async () => {
    if (!profileId || verificationLinkLoading) return;
    setVerificationLinkLoading(true);
    try {
      const returnUrl = `${window.location.origin}/seller-setup?step=5&refresh=true`;
      const { url } = await createStripeAccountLink(
        Number(profileId),
        returnUrl
      );
      handleClose();
      window.location.href = url;
    } catch (e) {
      setVerificationLinkLoading(false);
      snackbarRef.current?.showSnackbar(
        e instanceof Error
          ? e.message
          : "Failed to open verification page. Please try again.",
        "error"
      );
    }
  };

  const open = Boolean(anchorEl);
  const snackbarRef = useRef<SnackbarRef>(null);

  const sendEmailAgain = async () => {
    try {
      if (users && users.length > 0) {
        await sendConfirmationEmail(users[0].email, users[0].createdTime);
        snackbarRef.current?.showSnackbar(
          "Confirmation email resent. Please check your inbox.",
          "success"
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

  if (!loading && users && users.length <= 0) {
    deleteLoginCookieAndSession();
    navigate("/");
    return null;
  }

  return !users ? (
    <CenteredLoading />
  ) : (
    <>
      <SEO
        title={`My Account Page • FiddlePiddle • Toys Marketplace`}
        description={
          "Explore a wide range of toys on our marketplace. Buy, sell, and discover unique toys from collectors, sellers, and enthusiasts. Find your next favorite toy today!"
        }
        keywordsContent={`toys marketplace, buy toys, sell toys, collectible toys, vintage toys, kids toys, toy deals, rare toys, toy shop, toy sales, toy collectors`}
      />
      <div style={{ position: "relative", zIndex: 100 }}>
        <Breadcrumb
          items={[
            { text: "Home", url: "/" },
            { text: "My Account", url: "" },
          ]}
        />
      </div>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <SnackbarAlert ref={snackbarRef} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            Hi, {getSellerName(users[0].name)}!
          </Typography>

          <Box display="flex" alignItems="center" gap={2}>
            <IconButton color="secondary" onClick={handleOpen}>
              <Badge
                badgeContent={notifications.length}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    color: "white", // Set the text color to white
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "16px", // Adjust the value for roundness
                },
              }}
            >
              {notifications.length > 0 ? (
                <List
                  sx={{
                    width: 300,
                    maxHeight: 400,
                    overflow: "auto",
                    bgcolor: "background.paper",
                  }}
                >
                  {notifications.map((notification) => (
                    <React.Fragment key={notification.documentId}>
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() =>
                              handleDismissNotification(notification.documentId)
                            }
                          >
                            <Cancel />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={notification.title}
                          secondary={
                            <>
                              <Typography
                                sx={{ display: "inline" }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {notification.title.includes(
                                  "Confirm Your Account"
                                ) ? (
                                  <p>
                                    Check your email and click 'Confirm' within
                                    48 hours to activate your account, or it
                                    will be deleted.{" "}
                                    <Link
                                      onClick={sendEmailAgain}
                                      variant="body2"
                                      className="clickableLink"
                                    >
                                      Click here
                                    </Link>{" "}
                                    to resend it
                                  </p>
                                ) : notification.title.includes(
                                  "Seller Verification Required"
                                ) ? (
                                  <p>
                                    {notification.message}{" "}
                                    <Link
                                      onClick={handleCompleteVerificationOnStripe}
                                      variant="body2"
                                      className="clickableLink"
                                      sx={{ cursor: verificationLinkLoading ? "default" : "pointer" }}
                                    >
                                      {verificationLinkLoading ? "Opening…" : "Complete verification on Stripe"}
                                    </Link>
                                  </p>
                                ) : notification.title.includes(
                                  "Seller Account Approved"
                                ) ? (
                                  <p>
                                    {notification.message}{" "}
                                    <Link
                                      onClick={() => { handleClose(); navigate("/sell"); }}
                                      variant="body2"
                                      className="clickableLink"
                                      sx={{ cursor: "pointer" }}
                                    >
                                      Go to Seller Dashboard
                                    </Link>
                                  </p>
                                ) : (
                                  <>{notification.message}</>
                                )}
                              </Typography>
                              <br />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {notification.timeAgo}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography
                  sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
                >
                  You have no notifications at the moment
                </Typography>
              )}
            </Popover>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<LogoutIcon />}
              sx={{
                borderRadius: "20px",
                whiteSpace: "nowrap",
              }}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 3,
            // Add negative margin to break out of the container's padding on both sides
            mx: isMobile ? -2 : 0, // Adjust -2 based on the padding that the Container applies
          }}
        >
          {/* Floating Action Button */}
          <Tooltip title="Post a Toy for Sale" placement="left">
            <Fab
              color="primary"
              sx={{
                position: "fixed",
                bottom: isMobile ? 70 : 50,
                right: isMobile ? 16 : 50,
                zIndex: 99,
              }}
              onClick={() => navigate(sellRoute)}
            >
              <AddIcon sx={{ color: "white" }} />
            </Fab>
          </Tooltip>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons
            allowScrollButtonsMobile
            indicatorColor="primary"
            textColor="primary"
            sx={{
              width: "100%", // Ensure Tabs take up the full width
            }}
          >
            <Tab label="Overview" />
            <Tab label="Purchases" />
            <Tab label="Sold" />
            <Tab label="My Listings" />
            <Tab label="Wishlist" />
            <Tab label="Settings" />
          </Tabs>
        </Box>

        {/* Dashboard Tab */}
        {selectedTab === 0 ? (
          <Dashboard
            profileId={profileId}
            setSelectedTab={setSelectedTab}
            setDefaultFilterInMyListing={setDefaultFilterInMyListing}
          />
        ) : null}

        {/* Purchases Tab */}
        {selectedTab === 1 && <Purchase />}

        {/* Sold Tab */}
        {selectedTab === 2 && <Sold />}

        {/* My Listings Tab */}
        {selectedTab === 3 ? (
          <MyListing
            profileId={profileId}
            defaultFilter={defaultFilterInMyListing}
          />
        ) : null}

        {/* Wishlist Tab */}
        {selectedTab === 4 && <Wishlist profileId={profileId} />}

        {/* Settings Tab */}
        {selectedTab === 5 && <Settings userEmail={users[0].email} />}
      </Container>
    </>
  );
};

export default Account;
